import { Form, useFormikContext } from 'formik';
import { FormikDebounce } from '../common/form/FormikDebounce';
import { FC, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormContainer } from '../common/form/FormContainer';
import * as Yup from 'yup';
import { validations } from '../../utils/validations';
import { QcsButton } from '../common/basic/QcsButton';
import { ActivityAutocomplete } from '../common/form/ActivityAutocomplete';
import { EstablishmentAutocomplete } from '../common/form/EstablishmentAutocomplete';
import { WorkplaceAutocomplete } from '../common/form/WorkplaceAutocomplete';
import { InvitationCreateForm } from '../../models/invitation';

import { InvitationButtonsContainer } from './InvitationButtonsContainer';
import { workplaceService } from '@api/services/services';
import { EmployeeAutocomplete } from '../common/form/EmployeeAutocomplete';
import { EmployeeDto } from '@api/models/employees/EmployeeDto';
import { SupplierAutocomplete } from '../common/form/SupplierAutocomplete';
import { SupplierEmployeeAutocomplete } from '../common/form/SupplierEmployeeAutocomplete';
import { UserRole } from '@api/models/enums/UserRole';
import { UserState } from '@api/models/enums/UserState';
import { IconButton } from '@mui/material';
import { QcsSwitch } from '../common/form/QcsSwitch';

import { AddCircleOutline } from '@mui/icons-material';
import { FieldWithAction } from '../common/form/FieldWithAction';
import { DrawerContent } from '../common/DrawerContent';
import { RightDrawer } from '../common/RightDrawer';
import { SupplierDetail } from '../supplier/SupplierDetail';
import { SupplierEmployeeDetail } from '../supplier/SupplierEmployeeDetail';
import { SupplierDto } from '@api/models/suppliers/SupplierDto';
import { SupplierEmployeeDto } from '@api/models/suppliers/SupplierEmployeeDto';
import { useAppSelector } from '../../store';
import { selectCompanyCustomization } from '../../store/entities/companyCustomization';
import { InvitationButton } from './InvitationButton';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { InvitationVisitorsSection } from './InvitationVisitorsSection';
import { QcsTypography } from '../common/basic/QcsTypography';
import { ShowError } from '../ShowError';
import { resources } from '@api/resources';

const SupplierWatcher: FC = () => {
  const { values, setFieldValue } = useFormikContext<InvitationCreateForm>();
  const [firstCall, setFirstCall] = useState(true);

  useEffect(() => {
    if (values.supplier?.id && !firstCall) {
      setFieldValue('responsiblePerson', undefined);
    }
    setFirstCall(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.supplier?.id]);

  return null;
};

const WorkplaceWatcher: FC = () => {
  const { values, setFieldValue } = useFormikContext<InvitationCreateForm>();
  const [manuallyAddedEmployees, setManuallyAddedEmployees] = useState<EmployeeDto[]>([]);
  const [automaticallyAddedEmployees, setAutomaticallyAddedEmployees] = useState<EmployeeDto[]>([]);

  useEffect(() => {
    if (values.workplaces && values.workplaces.length > 0) {
      const workplaceIds = values.workplaces.map((workplace) => workplace.id);
      workplaceService.getWorkplaceNotificationEmployees(workplaceIds).then((it) => {
        setAutomaticallyAddedEmployees(it.data as any);

        // Combine both arrays and remove duplicates
        const combined = [...(it.data as any)];
        manuallyAddedEmployees.forEach((employee) => {
          if (!combined.some((existing) => existing.id === employee.id)) {
            combined.push(employee);
          }
        });

        setFieldValue('notifyEmployees', combined);
      });
    } else {
      setAutomaticallyAddedEmployees([]);
      setFieldValue('notifyEmployees', manuallyAddedEmployees);
    }
  }, [values.workplaces]);

  useEffect(() => {
    const newManuallyAdded: EmployeeDto[] = [];
    for (const ne of values.notifyEmployees ?? []) {
      if (!automaticallyAddedEmployees.some((auto) => auto.id === ne.id)) {
        newManuallyAdded.push(ne);
      }
    }
    setManuallyAddedEmployees(newManuallyAdded);
  }, [values.notifyEmployees]);

  return null;
};

interface Props {
  invitationData: InvitationCreateForm;
  handleGoBack: () => void;
  handleSubmit: (data: InvitationCreateForm) => void;
}

export const InvitationCreateStep2: FC<Props> = ({ invitationData, handleGoBack, handleSubmit }) => {
  const { t } = useTranslation();
  const [drawerContent, setDrawerContent] = useState<'supplier' | 'employee' | null>(null);
  const companyCustomization = useAppSelector(selectCompanyCustomization);
  const [pendingSubmitData, setPendingSubmitData] = useState<InvitationCreateForm | null>(null);

  const getVisitorsMissingEmail = (data: InvitationCreateForm): string[] => {
    const visitors: Array<{ name?: string; lastName?: string; email?: string }> = [
      { name: data.primaryVisitor?.name, lastName: data.primaryVisitor?.lastName, email: data.primaryVisitor?.email },
      ...(data.additionalVisitors ?? []),
    ];
    return visitors
      .filter((v) => !v.email)
      .map((v) => `${v.name ?? ''} ${v.lastName ?? ''}`.trim())
      .filter((n) => n.length > 0);
  };

  const handleSubmitWithEmailCheck = (data: InvitationCreateForm) => {
    if (data.isVisitor && getVisitorsMissingEmail(data).length > 0) {
      setPendingSubmitData(data);
      return;
    }
    return handleSubmit(data);
  };

  // Body of the "some visitors have no email" confirm dialog.
  // With 0–1 missing the generic message is enough; with 2+ we list names so
  // the user knows which rows to fix instead of having to hunt through the form.
  const renderMissingEmailMessage = (): ReactNode => {
    if (!pendingSubmitData) return t('invitation.create.missingEmailMessage');
    const missing = getVisitorsMissingEmail(pendingSubmitData);
    if (missing.length <= 1) return t('invitation.create.missingEmailMessage');
    return (
      <>
        {t('invitation.create.missingEmailMessageListIntro')}
        <ul>
          {missing.map((n) => (
            <li key={n}>
              <b>{n}</b>
            </li>
          ))}
        </ul>
        {t('invitation.create.missingEmailMessageListOutro')}
      </>
    );
  };

  return (
    <FormikDebounce<InvitationCreateForm>
      initialValues={invitationData}
      validationSchema={Yup.object().shape({
        establishments: validations.arrayNotEmpty(t),
        workplaces: validations.arrayNotEmpty(t),
        activities: validations.arrayNotEmpty(t),
        responsiblePerson: invitationData.isVisitor ? Yup.object().nullable() : validations.objectRequired(t),
        primaryVisitor: invitationData.isVisitor
          ? Yup.object()
              .shape({
                name: validations.stringRequired(t),
                lastName: validations.stringRequired(t),
                language: validations.stringRequired(t),
                email: validations.emailOptional(t),
                phone: validations.phoneOptional(t),
              })
              .nullable()
              .required(t('errors.required')!)
          : Yup.object().nullable(),
        additionalVisitors: invitationData.isVisitor
          ? Yup.array().of(
              Yup.object().shape({
                name: validations.stringRequired(t),
                lastName: validations.stringRequired(t),
                language: validations.stringRequired(t),
                email: validations.emailOptional(t),
                phone: validations.phoneOptional(t),
              }),
            )
          : Yup.array().nullable(),
        supplier: invitationData.isVisitor ? Yup.object().nullable() : validations.objectRequired(t),
      })}
      onSubmit={handleSubmitWithEmailCheck}
    >
      {({ values, setFieldValue, setSubmitting }) => {
        const handleAddSupplier = () => {
          setDrawerContent('supplier');
        };

        const handleAddResponsiblePerson = () => {
          setDrawerContent('employee');
        };

        const handleCloseDrawer = () => {
          setDrawerContent(null);
        };

        const handleSupplierCreated = (createdSupplier?: SupplierDto) => {
          if (createdSupplier) {
            setFieldValue('supplier', createdSupplier);
          }
          setDrawerContent(null);
        };

        const handleEmployeeCreated = (createdEmployee?: SupplierEmployeeDto) => {
          if (createdEmployee) {
            setFieldValue('responsiblePerson', createdEmployee);
          }
          setDrawerContent(null);
        };

        return (
          <>
            <Form>
              <WorkplaceWatcher />
              <SupplierWatcher />
              <FormContainer>
                <EstablishmentAutocomplete multiple name="establishments" label={t('invitation.establishments')} required />

                <WorkplaceAutocomplete
                  multiple
                  name="workplaces"
                  label={t('invitation.workplaces')}
                  required
                  estabilishments={values.establishments?.map((x) => x.id)}
                  showEstabilishmentName={true}
                />

                <ActivityAutocomplete
                  showCertificates
                  hideWithCertificates={values.isVisitor}
                  hideWithRisks={values.isVisitor}
                  multiple
                  name="activities"
                  label={t('invitation.activities')}
                  required
                />

                {values.isVisitor ? (
                  <>
                    <QcsTypography variant="h6">{t('invitation.create.step4OnceTitle')}</QcsTypography>
                    <InvitationVisitorsSection allowedLanguages={companyCustomization?.applicationLanguages} primaryPath="primaryVisitor" />
                  </>
                ) : (
                  <>
                    <FieldWithAction
                      action={
                        <IconButton onClick={handleAddSupplier} color="primary" sx={{ mt: '8px' }} title={t('invitation.addSupplier')}>
                          <AddCircleOutline />
                        </IconButton>
                      }
                    >
                      <SupplierAutocomplete name="supplier" label={t('invitation.supplier')} required />
                    </FieldWithAction>

                    <FieldWithAction
                      action={
                        <IconButton
                          onClick={handleAddResponsiblePerson}
                          color="primary"
                          sx={{ mt: '8px' }}
                          title={t('invitation.addResponsiblePerson')}
                          disabled={!values.supplier?.id}
                        >
                          <AddCircleOutline />
                        </IconButton>
                      }
                    >
                      <SupplierEmployeeAutocomplete
                        name="responsiblePerson"
                        label={t('invitation.responsibleSupplierPerson')}
                        supplierId={values.supplier?.id}
                        role={UserRole.ExternalManager}
                        state={[UserState.Active, UserState.Invited]}
                        disabled={!values.supplier?.id}
                        required
                      />
                    </FieldWithAction>

                    <QcsSwitch name="addWorkerToInvitation" label={t('invitation.addWorkerToInvitation')} />
                  </>
                )}

                {!values.disableVisitationNotification && (
                  <>
                    <QcsSwitch label={t('invitation.notifyEmployees')} name="isNotifyEmployees" />

                    {values.isNotifyEmployees && (
                      <EmployeeAutocomplete name="notifyEmployees" label={t('workplace.notificationEmployees')} filterOutNonHumanRoles multiple />
                    )}
                  </>
                )}

                <ShowError resource={resources.createInvitation} />
                <InvitationButtonsContainer>
                  <QcsButton variant="outlined" onClick={handleGoBack}>
                    {t('common.back')}
                  </QcsButton>
                  <div style={{ width: '100%' }} />
                  <InvitationButton />
                </InvitationButtonsContainer>
              </FormContainer>
            </Form>

            <RightDrawer open={drawerContent === 'supplier'} onClose={handleCloseDrawer} width={600}>
              {drawerContent === 'supplier' && (
                <DrawerContent>
                  <SupplierDetail createNew onSaved={handleSupplierCreated} />
                </DrawerContent>
              )}
            </RightDrawer>

            <RightDrawer open={drawerContent === 'employee'} onClose={handleCloseDrawer} width={600}>
              {drawerContent === 'employee' && values.supplier?.id && (
                <DrawerContent>
                  <SupplierEmployeeDetail
                    createNew
                    forSupplierId={values.supplier.id}
                    forceRole={UserRole.ExternalManager}
                    onSaved={handleEmployeeCreated}
                  />
                </DrawerContent>
              )}
            </RightDrawer>

            <ConfirmDialog
              onClose={() => {
                setPendingSubmitData(null);
                setSubmitting(false);
              }}
              onConfirm={pendingSubmitData ? () => handleSubmit(pendingSubmitData) : null}
              title={t('invitation.create.missingEmailTitle')}
              message={renderMissingEmailMessage()}
              confirmMessage={t('invitation.create.missingEmailEnterEmail')}
              cancelText={t('invitation.create.missingEmailContinue')}
            />
          </>
        );
      }}
    </FormikDebounce>
  );
};
